import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { B, FONT, fmtDate } from '../lib/constants';

const CATEGORIES = [
  'Appliances', 'Tile', 'Lighting/Electrical', 'Bathroom Fixtures and Hardware',
  'Door and Cabinet Hardware', 'Mirrors/Towel Hooks', 'Other'
];

const STATUS_COLORS = {
  needed: { bg: 'rgba(248,218,107,0.1)', border: '#9A732F', label: 'Needed', text: '#F8DA6B' },
  ordered: { bg: 'rgba(59,130,246,0.1)', border: '#3B82F6', label: 'Ordered', text: '#60A5FA' },
  on_site: { bg: 'rgba(76,175,61,0.1)', border: '#4CAF7D', label: 'On Site', text: '#4CAF7D' },
};

const STATUS_ORDER = ['needed', 'ordered', 'on_site'];


// Android back button closes the topmost open box instead of leaving the page
function useBackToClose(open, close) {
  useEffect(() => {
    if (!open) return;
    window.history.pushState({ mhModal: true }, '');
    const onPop = () => close();
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      // Closing via a button: neutralize the marker without navigating anywhere
      if (window.history.state && window.history.state.mhModal) window.history.replaceState({}, '');
    };
  }, [open]);
}

export default function PurchasesTab({ project, isBuilder = true }) {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  useBackToClose(showAdd, () => setShowAdd(false));
  useBackToClose(!!selected, () => setSelected(null));
  const [checked, setChecked] = useState(new Set());
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => { load(); }, [project.id]);
  useEffect(() => { setChecked(new Set()); }, [activeCat]);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase.from('purchases').select('*').eq('project_id', project.id).order('category').order('created_at');
    if (data) {
      setPurchases(data);
      if (isBuilder) checkAutoNotifications(data);
    }
    setLoading(false);
  };

  const checkAutoNotifications = async (items) => {
    const today = new Date(); today.setHours(0,0,0,0);
    const toNotify = items.filter(p => {
      if (!p.needed_by || p.status === 'on_site') return false;
      const needed = new Date(p.needed_by + 'T12:00:00');
      const daysAway = Math.round((needed - today) / 86400000);
      const key = `purchase_notified_${p.id}_${daysAway <= 3 ? '3d' : '14d'}`;
      if (localStorage.getItem(key)) return false;
      if (daysAway === 14 || daysAway === 3) {
        localStorage.setItem(key, '1');
        return true;
      }
      return false;
    });
    if (toNotify.length === 0) return;
    const { notifyProjectClients } = await import('../hooks/usePushNotifications');
    const urgent = toNotify.some(p => {
      const d = Math.round((new Date(p.needed_by + 'T12:00:00') - today) / 86400000);
      return d <= 3;
    });
    await notifyProjectClients(
      project.id,
      urgent ? `⚠️ Purchases Needed Soon` : `Upcoming Purchases — ${project.name}`,
      toNotify.map(p => p.item + (p.needed_by ? ` by ${fmtDate(p.needed_by)}` : '')).slice(0, 3).join(', '),
      `${window.location.origin}?project=${project.id}&tab=purchases`
    );
  };

  // ============================================================
  // CLIENT VIEW — flat list grouped by status (like Decisions tab)
  // ============================================================
  if (!isBuilder) {
    const groups = STATUS_ORDER
      .map(s => ({ status: s, items: purchases.filter(p => p.status === s) }))
      .filter(g => g.items.length > 0);

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ color: B.white, fontSize: 18, fontWeight: 700 }}>Purchases</div>
          <button onClick={() => setShowAdd(true)}
            style={{ background: B.goldlt, color: B.black, border: 'none', borderRadius: 9, padding: '8px 16px', cursor: 'pointer', fontSize: 12, fontWeight: 800, fontFamily: FONT }}>
            + Add
          </button>
        </div>

        {loading ? (
          <div style={{ color: B.muted, textAlign: 'center', padding: 32 }}>Loading…</div>
        ) : purchases.length === 0 ? (
          <div style={{ background: B.navy, borderRadius: 14, padding: 36, textAlign: 'center', border: `1px solid ${B.slate}` }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>📦</div>
            <div style={{ color: B.muted, fontSize: 13 }}>No purchases yet.</div>
          </div>
        ) : (
          groups.map(({ status, items }) => (
            <div key={status} style={{ marginBottom: 24 }}>
              <div style={{ color: B.muted, fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 12 }}>
                {STATUS_COLORS[status].label} · {items.length}
              </div>
              {items.map(p => (
                <ClientPurchaseCard key={p.id} purchase={p} onTap={() => setSelected(p)} onUpdate={load} />
              ))}
            </div>
          ))
        )}

        {showAdd && (
          <AddPurchaseModal project={project} isBuilder={false} defaultCategory={null}
            onClose={() => setShowAdd(false)} onSave={() => { setShowAdd(false); load(); }} />
        )}
        {selected && (
          <PurchaseDetailModal purchase={selected} project={project} isBuilder={false}
            onClose={() => setSelected(null)} onUpdate={() => { setSelected(null); load(); }} />
        )}
      </div>
    );
  }

  // ============================================================
  // BUILDER VIEW — original category grid + drill-down
  // ============================================================
  const catCounts = {};
  CATEGORIES.forEach(c => { catCounts[c] = { total: 0, needed: 0 }; });
  purchases.forEach(p => {
    const cat = p.category || 'Other';
    if (catCounts[cat]) {
      catCounts[cat].total++;
      if (p.status === 'needed') catCounts[cat].needed++;
    }
  });

  const filtered = activeCat ? purchases.filter(p => p.category === activeCat) : [];

  const toggleCheck = (id) => {
    setChecked(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (checked.size === filtered.length) setChecked(new Set());
    else setChecked(new Set(filtered.map(p => p.id)));
  };

  const sendSelected = async () => {
    if (checked.size === 0) return;
    setSending(true);
    const items = purchases.filter(p => checked.has(p.id));
    const { notifyProjectClients } = await import('../hooks/usePushNotifications');
    const title = `${project.name} — ${items.length} Purchase${items.length !== 1 ? 's' : ''} Needed`;
    const body = items.slice(0, 3).map(i => i.item + (i.room ? ` (${i.room})` : '')).join(', ') + (items.length > 3 ? `… +${items.length - 3} more` : '');
    await notifyProjectClients(project.id, title, body, `${window.location.origin}?project=${project.id}&tab=purchases`);
    setSending(false);
    setSent(true);
    setChecked(new Set());
    setTimeout(() => setSent(false), 3000);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <div style={{ color: B.white, fontSize: 18, fontWeight: 700 }}>Purchases</div>
          {activeCat && (
            <button onClick={() => setActiveCat(null)}
              style={{ background: 'none', border: 'none', color: B.goldlt, fontSize: 12, cursor: 'pointer', padding: 0, fontFamily: FONT }}>
              ‹ All Categories
            </button>
          )}
        </div>
        <button onClick={() => setShowAdd(true)}
          style={{ background: B.goldlt, color: B.black, border: 'none', borderRadius: 9, padding: '8px 16px', cursor: 'pointer', fontSize: 12, fontWeight: 800, fontFamily: FONT }}>
          + Add
        </button>
      </div>

      {loading ? (
        <div style={{ color: B.muted, textAlign: 'center', padding: 32 }}>Loading…</div>
      ) : !activeCat ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10 }}>
          {CATEGORIES.map(cat => {
            const { total, needed } = catCounts[cat] || { total: 0, needed: 0 };
            return (
              <button key={cat} onClick={() => setActiveCat(cat)}
                style={{ background: B.navy, border: `1px solid ${needed > 0 ? B.gold : B.slate}`, borderRadius: 14, padding: 16, cursor: 'pointer', textAlign: 'left', fontFamily: FONT }}>
                <div style={{ fontSize: 24, marginBottom: 8 }}>{CAT_ICONS[cat] || '📦'}</div>
                <div style={{ color: B.white, fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{cat}</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <div style={{ color: B.muted, fontSize: 11 }}>{total} items</div>
                  {needed > 0 && <div style={{ background: B.goldlt, color: B.black, borderRadius: 8, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>{needed} needed</div>}
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div>
          {isBuilder && filtered.length > 0 && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
              <button onClick={toggleAll}
                style={{ background: 'transparent', border: `1px solid ${B.slate}`, borderRadius: 7, padding: '6px 10px', cursor: 'pointer', color: B.muted, fontSize: 11, fontFamily: FONT }}>
                {checked.size === filtered.length ? 'Deselect All' : 'Select All'}
              </button>
              {checked.size > 0 && (
                <button onClick={sendSelected} disabled={sending}
                  style={{ flex: 1, background: sent ? 'rgba(76,175,61,0.15)' : B.goldlt, color: sent ? '#4CAF7D' : B.black, border: `1px solid ${sent ? '#4CAF7D' : 'transparent'}`, borderRadius: 7, padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 800, fontFamily: FONT }}>
                  {sending ? 'Sending…' : sent ? '✓ Sent!' : `🔔 Send ${checked.size} to Client`}
                </button>
              )}
            </div>
          )}
          {filtered.length === 0 ? (
            <div style={{ background: B.navy, borderRadius: 14, padding: 36, textAlign: 'center', border: `1px solid ${B.slate}` }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📦</div>
              <div style={{ color: B.muted, fontSize: 13 }}>No purchases in {activeCat} yet.</div>
            </div>
          ) : filtered.map(p => (
            <PurchaseCard key={p.id} purchase={p} isBuilder={isBuilder}
              checked={checked.has(p.id)}
              onCheck={isBuilder ? () => toggleCheck(p.id) : null}
              onTap={() => setSelected(p)} onUpdate={load} />
          ))}
        </div>
      )}

      {showAdd && (
        <AddPurchaseModal project={project} isBuilder={isBuilder} defaultCategory={activeCat}
          onClose={() => setShowAdd(false)} onSave={() => { setShowAdd(false); load(); }} />
      )}
      {selected && (
        <PurchaseDetailModal purchase={selected} project={project} isBuilder={isBuilder}
          onClose={() => setSelected(null)} onUpdate={() => { setSelected(null); load(); }} />
      )}
    </div>
  );
}

const CAT_ICONS = {
  Appliances: '🍳', Tile: '🧱', 'Lighting/Electrical': '💡',
  'Bathroom Fixtures and Hardware': '🛁', 'Door and Cabinet Hardware': '🚪',
  'Mirrors/Towel Hooks': '🪞', Other: '📦',
};

// Client card — styled like a Decision card in ClientPortal
async function uploadPurchasePhoto(projectId, file) {
  const path = `${projectId}/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
  const { error } = await supabase.storage.from('purchase-photos').upload(path, file);
  if (error) throw error;
  const { data } = supabase.storage.from('purchase-photos').getPublicUrl(path);
  return data.publicUrl;
}

function ClientPurchaseCard({ purchase: p, onTap, onUpdate }) {
  const st = STATUS_COLORS[p.status] || STATUS_COLORS.needed;
  const isClientPurchase = p.purchased_by === 'client';

  const handleTogglePurchased = async (e) => {
    e.stopPropagation();
    const nowPurchased = !p.client_purchased;
    await supabase.from('purchases').update({
      client_purchased: nowPurchased,
      status: nowPurchased ? 'ordered' : 'needed'
    }).eq('id', p.id);
    if (nowPurchased) {
      try {
        const { notifyBuilder } = await import('../hooks/usePushNotifications');
        await notifyBuilder('🛒 Client Purchased Item', `${p.item} marked as purchased`, `${window.location.origin}`);
      } catch(err) {}
    }
    onUpdate();
  };

  return (
    <div onClick={onTap}
      style={{
        background: '#0F0F0F', borderRadius: 12, padding: 16, marginBottom: 10,
        border: `1px solid ${st.border}`, borderLeft: `3px solid ${st.text}`,
        cursor: 'pointer', fontFamily: FONT
      }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 6 }}>
        <div style={{ color: B.white, fontSize: 14, fontWeight: 700, flex: 1 }}>{p.item}</div>
        <div style={{ background: st.bg, border: `1px solid ${st.border}`, borderRadius: 8, padding: '3px 9px', fontSize: 10, fontWeight: 700, color: st.text, flexShrink: 0 }}>
          {st.label}
        </div>
      </div>
      {p.category && (
        <div style={{ color: B.goldlt, fontSize: 12, marginBottom: 4 }}>
          {p.category}{p.room ? ` · ${p.room}` : ''}
        </div>
      )}
      {p.notes && <div style={{ color: '#888', fontSize: 12, lineHeight: 1.4, marginBottom: 4 }}>{p.notes}</div>}
      {p.url && (
        <div onClick={e => { e.stopPropagation(); window.open(p.url, '_blank'); }}
          style={{ color: B.goldlt, fontSize: 12, marginBottom: 4, textDecoration: 'underline' }}>
          🔗 View Link
        </div>
      )}
      {p.needed_by && <div style={{ color: '#888', fontSize: 11, marginTop: 4 }}>Needed on site by {fmtDate(p.needed_by)}</div>}
      <div style={{ color: p.purchased_by === 'muller' ? B.goldlt : B.muted, fontSize: 10, fontWeight: 700, marginTop: 6 }}>
        {p.purchased_by === 'muller' ? '🏗 Muller purchases' : '👤 You purchase'}
      </div>
      {(() => {
        const isPurchased = p.client_purchased || ['purchased', 'ordered', 'on_site'].includes(p.status);
        const who = p.client_purchased ? 'You' : (p.purchased_by === 'muller' ? 'Muller' : p.purchased_by === 'client' ? 'You' : 'Muller');
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, paddingTop: 8, borderTop: '1px solid #1E1E24' }}>
            <span style={{ fontSize: 11 }}>{isPurchased ? '✅' : '🔲'}</span>
            <span style={{ color: isPurchased ? '#4CAF7D' : B.goldlt, fontSize: 11, fontWeight: 700 }}>
              {isPurchased ? `Purchased by ${who}` : 'Not purchased yet'}
            </span>
          </div>
        );
      })()}

      {isClientPurchase && (
        <div onClick={handleTogglePurchased}
          style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, paddingTop: 10, borderTop: `1px solid ${B.slate}` }}>
          <div style={{
            width: 22, height: 22, borderRadius: 6,
            border: `2px solid ${p.client_purchased ? '#4CAF7D' : B.gold}`,
            background: p.client_purchased ? '#4CAF7D' : 'transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
          }}>
            {p.client_purchased && <span style={{ color: '#fff', fontSize: 13, fontWeight: 800 }}>✓</span>}
          </div>
          <div style={{ color: p.client_purchased ? '#4CAF7D' : B.white, fontSize: 13, fontWeight: 600 }}>
            {p.client_purchased ? 'I have purchased this' : 'Mark as purchased'}
          </div>
        </div>
      )}
    </div>
  );
}

function PurchaseCard({ purchase: p, isBuilder, checked, onCheck, onTap, onUpdate }) {
  const st = STATUS_COLORS[p.status] || STATUS_COLORS.needed;
  const handleTogglePurchased = async (e) => {
    e.stopPropagation();
    const nowPurchased = !p.client_purchased;
    await supabase.from('purchases').update({ client_purchased: nowPurchased, status: nowPurchased ? 'ordered' : 'needed' }).eq('id', p.id);
    if (nowPurchased && !isBuilder) {
      try {
        const { notifyBuilder } = await import('../hooks/usePushNotifications');
        await notifyBuilder('🛒 Client Purchased Item', `${p.item} marked as purchased`, `${window.location.origin}`);
      } catch(err) {}
    }
    onUpdate();
  };

  return (
    <div style={{ background: B.navy, borderRadius: 12, padding: '14px 16px', marginBottom: 8, border: `1px solid ${checked ? B.goldlt : st.border}`, cursor: 'pointer', transition: 'border-color 0.15s' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        {onCheck && (
          <div onClick={e => { e.stopPropagation(); onCheck(); }}
            style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${checked ? B.goldlt : B.slate}`, background: checked ? B.goldlt : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
            {checked && <span style={{ color: B.black, fontSize: 13, fontWeight: 800 }}>✓</span>}
          </div>
        )}
        <div style={{ flex: 1 }} onClick={onTap}>
          <div style={{ color: B.white, fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{p.item}</div>
          {p.url && <div style={{ color: B.goldlt, fontSize: 12, marginBottom: 3, textDecoration: 'underline' }} onClick={e => { e.stopPropagation(); window.open(p.url, '_blank'); }}>🔗 View Link</div>}
          {p.needed_by && <div style={{ color: B.goldlt, fontSize: 11, marginBottom: 3 }}>📅 Needed on site: {fmtDate(p.needed_by)}</div>}
        </div>
        <div onClick={onTap} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          <div style={{ background: st.bg, border: `1px solid ${st.border}`, borderRadius: 8, padding: '3px 9px', fontSize: 10, fontWeight: 700, color: st.text }}>{st.label}</div>
          <div style={{ color: p.purchased_by === 'muller' ? B.goldlt : B.muted, fontSize: 10, fontWeight: 700 }}>
            {p.purchased_by === 'muller' ? '🏗 Muller' : '👤 Client'}
          </div>
        </div>
      </div>
      {(() => {
        const isPurchased = p.client_purchased || ['purchased', 'ordered', 'on_site'].includes(p.status);
        const who = p.client_purchased ? 'Client' : (p.purchased_by === 'muller' ? 'Muller' : p.purchased_by === 'client' ? 'Client' : 'Muller');
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, paddingTop: 8, borderTop: '1px solid #1E1E24' }}>
            <span style={{ fontSize: 11 }}>{isPurchased ? '✅' : '🔲'}</span>
            <span style={{ color: isPurchased ? '#4CAF7D' : B.goldlt, fontSize: 11, fontWeight: 700 }}>
              {isPurchased ? `Purchased by ${who}` : 'Not purchased yet'}
            </span>
          </div>
        );
      })()}
      {/* Client purchased checkbox */}
      {p.purchased_by === 'client' && !isBuilder && (
        <div onClick={handleTogglePurchased}
          style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, paddingTop: 10, borderTop: `1px solid ${B.slate}` }}>
          <div style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${p.client_purchased ? '#4CAF7D' : B.gold}`, background: p.client_purchased ? '#4CAF7D' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {p.client_purchased && <span style={{ color: '#fff', fontSize: 13, fontWeight: 800 }}>✓</span>}
          </div>
          <div style={{ color: p.client_purchased ? '#4CAF7D' : B.white, fontSize: 13, fontWeight: 600 }}>
            {p.client_purchased ? 'I have purchased this' : 'Mark as purchased'}
          </div>
        </div>
      )}
    </div>
  );
}

function PurchaseDetailModal({ purchase: p, project, isBuilder, onClose, onUpdate }) {
  const [form, setForm] = useState({ ...p, room: p.room || '' });
  const [saving, setSaving] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [fullPhoto, setFullPhoto] = useState(null);
  useBackToClose(!!fullPhoto, () => setFullPhoto(null));
  const [notifying, setNotifying] = useState(false);
  const [notified, setNotified] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    let photo_url = form.photo_url || null;
    if (photoFile) {
      setUploadingPhoto(true);
      try { photo_url = await uploadPurchasePhoto(project.id, photoFile); } catch(e) { console.error('Photo upload failed:', e); }
      setUploadingPhoto(false);
    }
    await supabase.from('purchases').update({
      item: form.item, notes: form.notes, url: form.url,
      category: form.category || 'Other',
      needed_by: form.needed_by || null,
      status: form.status, purchased_by: form.purchased_by, client_purchased: form.client_purchased,
      photo_url,
    }).eq('id', p.id);
    setSaving(false);
    onUpdate();
  };

  const handleDelete = async () => {
    await supabase.from('purchases').delete().eq('id', p.id);
    onUpdate();
  };

  const handleNotify = async () => {
    setNotifying(true);
    const { notifyProjectClients } = await import('../hooks/usePushNotifications');
    const dateStr = form.needed_by ? ` — needed on site by ${fmtDate(form.needed_by)}` : '';
    await notifyProjectClients(project.id, `Purchase Needed On Site`, `${form.item} is needed on site${dateStr}`, `${window.location.origin}?project=${project.id}&tab=purchases`);
    await supabase.from('purchases').update({ notify_sent: true }).eq('id', p.id);
    setNotifying(false);
    setNotified(true);
    setTimeout(() => setNotified(false), 3000);
  };

  const inputStyle = { width: '100%', background: B.mid, border: `1px solid ${B.slate}`, borderRadius: 9, padding: '10px 12px', color: B.white, fontSize: 13, outline: 'none', fontFamily: FONT, boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 2000, display: 'flex', alignItems: 'flex-end', fontFamily: FONT }}
      onClick={onClose}>
      <div style={{ background: B.dark, borderRadius: '20px 20px 0 0', padding: 24, width: '100%', maxHeight: '90vh', overflowY: 'auto', boxSizing: 'border-box' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ color: B.white, fontSize: 17, fontWeight: 800 }}>{p.item}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: B.muted, fontSize: 24, cursor: 'pointer', padding: 0 }}>×</button>
        </div>

        {/* Status */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ color: B.muted, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>Status</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {['needed', 'ordered', 'on_site'].map(s => (
              <button key={s} onClick={() => set('status', s)}
                style={{ flex: 1, background: form.status === s ? STATUS_COLORS[s].bg : B.mid, border: `1px solid ${form.status === s ? STATUS_COLORS[s].border : B.slate}`, borderRadius: 8, padding: '8px 4px', cursor: 'pointer', color: form.status === s ? STATUS_COLORS[s].text : B.muted, fontSize: 11, fontWeight: 700, fontFamily: FONT }}>
                {STATUS_COLORS[s].label}
              </button>
            ))}
          </div>
        </div>

        {/* Who purchases */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ color: B.muted, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>Purchased By</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {[{ v: 'client', label: '👤 Client' }, { v: 'muller', label: '🏗 Muller' }].map(opt => (
              <button key={opt.v} onClick={() => set('purchased_by', opt.v)}
                style={{ flex: 1, background: form.purchased_by === opt.v ? 'rgba(248,218,107,0.1)' : B.mid, border: `1px solid ${form.purchased_by === opt.v ? B.gold : B.slate}`, borderRadius: 8, padding: '9px', cursor: 'pointer', color: form.purchased_by === opt.v ? B.goldlt : B.muted, fontSize: 12, fontWeight: 700, fontFamily: FONT }}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Client purchased checkbox */}
        <div onClick={() => set('client_purchased', !form.client_purchased)}
          style={{ display: 'flex', alignItems: 'center', gap: 10, background: B.navy, borderRadius: 10, padding: '12px 14px', marginBottom: 14, cursor: 'pointer', border: `1px solid ${form.client_purchased ? '#4CAF7D' : B.slate}` }}>
          <div style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${form.client_purchased ? '#4CAF7D' : B.gold}`, background: form.client_purchased ? '#4CAF7D' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {form.client_purchased && <span style={{ color: '#fff', fontSize: 13, fontWeight: 800 }}>✓</span>}
          </div>
          <div style={{ color: form.client_purchased ? '#4CAF7D' : B.white, fontSize: 13, fontWeight: 600 }}>Client has purchased this</div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ color: B.muted, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>Category</div>
          <select value={form.category || 'Other'} onChange={e => set('category', e.target.value)} style={inputStyle}>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ color: B.muted, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>Item</div>
          <input value={form.item} onChange={e => set('item', e.target.value)}
            placeholder="e.g. Kitchen Faucet — Master Bath"
            style={{ ...inputStyle, border: `1px solid ${form.item ? B.gold : B.slate}` }} />
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ color: B.muted, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>Photo</div>
          {(photoPreview || form.photo_url) && (
            <img src={photoPreview || form.photo_url} alt=""
              onClick={() => setFullPhoto(photoPreview || form.photo_url)}
              style={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 10, marginBottom: 8, border: `1px solid ${B.slate}`, cursor: 'zoom-in' }} />
          )}
          {fullPhoto && (
            <div onClick={() => setFullPhoto(null)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' }}>
              <img src={fullPhoto} alt="" style={{ maxWidth: '100vw', maxHeight: '100vh', objectFit: 'contain' }} />
              <button onClick={() => setFullPhoto(null)}
                style={{ position: 'fixed', top: 18, right: 18, background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '50%', width: 40, height: 40, color: '#fff', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
          )}
          <label style={{ display: 'block', border: `1px dashed ${B.slate}`, borderRadius: 10, padding: '10px 14px', cursor: 'pointer', color: B.muted, fontSize: 12, textAlign: 'center' }}>
            📷 {form.photo_url || photoPreview ? 'Replace photo' : 'Add a photo'}
            <input type="file" accept="image/*" style={{ display: 'none' }}
              onChange={e => {
                const file = e.target.files?.[0];
                if (file) { setPhotoFile(file); setPhotoPreview(URL.createObjectURL(file)); }
              }} />
          </label>
          <AttachmentBlock table="purchases" row={p} onUpdate={onUpdate} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          <div>
            <div style={{ color: B.muted, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>Link</div>
            <input value={form.url || ''} onChange={e => set('url', e.target.value)} placeholder="https://…" style={inputStyle} />
          </div>
          <div>
            <div style={{ color: B.muted, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>Needed On Site</div>
            <input type="date" value={form.needed_by || ''} onChange={e => set('needed_by', e.target.value)} style={inputStyle} />
          </div>
        </div>
        <div style={{ marginBottom: 18 }}>
          <div style={{ color: B.muted, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>Notes</div>
          <textarea value={form.notes || ''} onChange={e => set('notes', e.target.value)} rows={3} placeholder="Specs, model numbers, color…"
            style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }} />
        </div>

        {/* Notify client button */}
        {isBuilder && (
          <button onClick={handleNotify} disabled={notifying}
            style={{ width: '100%', background: notified ? 'rgba(76,175,61,0.15)' : 'rgba(248,218,107,0.08)', border: `1px solid ${notified ? '#4CAF7D' : B.gold}`, borderRadius: 10, padding: 12, cursor: 'pointer', color: notified ? '#4CAF7D' : B.goldlt, fontSize: 13, fontWeight: 700, fontFamily: FONT, marginBottom: 10 }}>
            {notifying ? 'Notifying…' : notified ? '✓ Client Notified!' : `🔔 Notify Client${form.needed_by ? ' — Needed by ' + fmtDate(form.needed_by) : ''}`}
          </button>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={handleSave} disabled={saving}
            style={{ flex: 2, background: B.goldlt, color: B.black, border: 'none', borderRadius: 11, padding: 14, cursor: 'pointer', fontSize: 14, fontWeight: 800, fontFamily: FONT }}>
            {uploadingPhoto ? 'Uploading photo…' : saving ? 'Saving…' : 'Save Changes'}
          </button>
          {isBuilder && (
            <button onClick={handleDelete}
              style={{ flex: 1, background: 'transparent', border: '1px solid #E74C3C', borderRadius: 11, padding: 14, cursor: 'pointer', color: '#E74C3C', fontSize: 13, fontFamily: FONT }}>
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function AddPurchaseModal({ project, isBuilder, defaultCategory, defaultStatus = 'needed', onClose, onSave }) {
  const [form, setForm] = useState({ item: '', room: '', category: defaultCategory || 'Appliances', notes: '', url: '', needed_by: '', purchased_by: 'client', status: defaultStatus });
  const [saving, setSaving] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.item.trim()) return;
    setSaving(true);
    let photo_url = null;
    if (photoFile) {
      try { photo_url = await uploadPurchasePhoto(project.id, photoFile); } catch(e) { console.error('Photo upload failed:', e); }
    }
    await supabase.from('purchases').insert({
      project_id: parseInt(project.id),
      item: form.item.trim(),
      category: form.category,
      notes: form.notes || null,
      url: form.url || null,
      needed_by: form.needed_by || null,
      purchased_by: form.purchased_by,
      status: form.status,
      added_by: isBuilder ? 'builder' : 'client',
      photo_url,
    });
    setSaving(false);
    onSave();
  };

  const inputStyle = { width: '100%', background: B.mid, border: `1px solid ${B.slate}`, borderRadius: 9, padding: '10px 12px', color: B.white, fontSize: 13, outline: 'none', fontFamily: FONT, boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 2000, display: 'flex', alignItems: 'flex-end', fontFamily: FONT }}
      onClick={onClose}>
      <div style={{ background: B.dark, borderRadius: '20px 20px 0 0', padding: 24, width: '100%', maxHeight: '90vh', overflowY: 'auto', boxSizing: 'border-box' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ color: B.white, fontSize: 17, fontWeight: 800 }}>Add Purchase</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: B.muted, fontSize: 24, cursor: 'pointer', padding: 0 }}>×</button>
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {[
              { v: 'needed', label: '🛒 Need to Purchase' },
              { v: 'purchased', label: '✓ Purchased' },
            ].map(opt => (
              <button key={opt.v} onClick={() => set('status', opt.v)}
                style={{ background: form.status === opt.v ? 'rgba(248,218,107,0.12)' : 'transparent', border: `1px solid ${form.status === opt.v ? B.goldlt : B.slate}`, borderRadius: 10, padding: '12px 8px', cursor: 'pointer', color: form.status === opt.v ? B.goldlt : B.muted, fontSize: 12, fontWeight: 800, fontFamily: FONT }}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ color: B.muted, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>Category</div>
          <select value={form.category} onChange={e => set('category', e.target.value)} style={inputStyle}>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ color: B.muted, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>Item *</div>
          <input value={form.item} onChange={e => set('item', e.target.value)}
            placeholder="e.g. Kitchen Faucet — Master Bath"
            autoFocus style={{ ...inputStyle, border: `1px solid ${form.item ? B.gold : B.slate}` }} />
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ color: B.muted, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>Photo (optional)</div>
          {photoPreview && (
            <img src={photoPreview} alt=""
              style={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 10, marginBottom: 8, border: `1px solid ${B.slate}` }} />
          )}
          <label style={{ display: 'block', border: `1px dashed ${B.slate}`, borderRadius: 10, padding: '10px 14px', cursor: 'pointer', color: B.muted, fontSize: 12, textAlign: 'center' }}>
            📷 {photoPreview ? 'Replace photo' : 'Add a photo'}
            <input type="file" accept="image/*" style={{ display: 'none' }}
              onChange={e => {
                const file = e.target.files?.[0];
                if (file) { setPhotoFile(file); setPhotoPreview(URL.createObjectURL(file)); }
              }} />
          </label>
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ color: B.muted, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>Purchased By</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {[{ v: 'client', label: '👤 Client' }, { v: 'muller', label: '🏗 Muller' }].map(opt => (
              <button key={opt.v} onClick={() => set('purchased_by', opt.v)}
                style={{ flex: 1, background: form.purchased_by === opt.v ? 'rgba(248,218,107,0.1)' : B.mid, border: `1px solid ${form.purchased_by === opt.v ? B.gold : B.slate}`, borderRadius: 8, padding: '9px', cursor: 'pointer', color: form.purchased_by === opt.v ? B.goldlt : B.muted, fontSize: 12, fontWeight: 700, fontFamily: FONT }}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          <div>
            <div style={{ color: B.muted, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>Link</div>
            <input value={form.url} onChange={e => set('url', e.target.value)} placeholder="https://…" style={inputStyle} />
          </div>
          <div>
            <div style={{ color: B.muted, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>Needed On Site</div>
            <input type="date" value={form.needed_by} onChange={e => set('needed_by', e.target.value)} style={inputStyle} />
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ color: B.muted, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>Notes</div>
          <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={3} placeholder="Specs, model numbers, color, link…"
            style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }} />
        </div>

        <button onClick={handleSave} disabled={saving || !form.item.trim()}
          style={{ width: '100%', background: form.item.trim() ? B.goldlt : B.slate, color: form.item.trim() ? B.black : B.muted, border: 'none', borderRadius: 12, padding: 15, cursor: form.item.trim() ? 'pointer' : 'default', fontSize: 15, fontWeight: 800, fontFamily: FONT }}>
          {saving ? 'Saving…' : 'Add Purchase'}
        </button>
      </div>
    </div>
  );
}


function AttachmentBlock({ table, row, onUpdate }) {
  const [uploading, setUploading] = useState(false);
  const [dragHover, setDragHover] = useState(false);
  const [list, setList] = useState(Array.isArray(row.attachments) ? row.attachments : []);

  const addFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const path = `files/${table}_${row.id}_${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
      const { error } = await supabase.storage.from('purchase-photos').upload(path, file);
      if (!error) {
        const { data } = supabase.storage.from('purchase-photos').getPublicUrl(path);
        const next = [...list, { name: file.name, url: data.publicUrl }];
        await supabase.from(table).update({ attachments: next }).eq('id', row.id);
        setList(next);
      }
    } catch(e) { console.error('Attachment upload failed:', e); }
    setUploading(false);
  };

  const removeFile = async (i) => {
    const next = list.filter((_, idx) => idx !== i);
    await supabase.from(table).update({ attachments: next.length ? next : null }).eq('id', row.id);
    setList(next);
  };

  return (
    <div style={{ marginTop: 8 }}>
      {list.map((a, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, background: B.mid, border: `1px solid ${B.slate}`, borderRadius: 9, padding: '8px 11px', marginBottom: 6 }}>
          <span style={{ fontSize: 13 }}>📎</span>
          <a href={a.url} target="_blank" rel="noreferrer"
            style={{ flex: 1, color: '#4FA3E0', fontSize: 12, textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {a.name}
          </a>
          <button onClick={() => removeFile(i)}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: B.muted, fontSize: 14, padding: 0 }}>×</button>
        </div>
      ))}
      <label
        onDragOver={e => { e.preventDefault(); setDragHover(true); }}
        onDragLeave={() => setDragHover(false)}
        onDrop={e => { e.preventDefault(); setDragHover(false); Array.from(e.dataTransfer.files || []).forEach(fl => addFile(fl)); }}
        style={{ display: 'block', border: `1px dashed ${dragHover ? B.goldlt : B.slate}`, background: dragHover ? 'rgba(248,218,107,0.06)' : 'transparent', borderRadius: 10, padding: '10px 14px', cursor: 'pointer', color: dragHover ? B.goldlt : B.muted, fontSize: 12, textAlign: 'center' }}>
        📎 {uploading ? 'Uploading…' : 'Attach a file — drag & drop or tap'}
        <input type="file" multiple style={{ display: 'none' }}
          onChange={e => { Array.from(e.target.files || []).forEach(fl => addFile(fl)); e.target.value = ''; }} />
      </label>
    </div>
  );
}
